import { type CSSProperties, useMemo } from 'react';
import { NavIcon } from './shared';
import type { PatientVitalEntryWithDefinitionRow } from '@helios/data-access';

// ─── Types ───

type LatestVitalsGrouped = {
  primary: PatientVitalEntryWithDefinitionRow[];
  additional: PatientVitalEntryWithDefinitionRow[];
  recordedAt: string;
};

// ─── Helpers ───

function formatVitalValue(row: PatientVitalEntryWithDefinitionRow): string {
  switch (row.value_type) {
    case 'number':
    case 'integer':
      return row.value_number != null ? String(row.value_number) : '--';
    case 'text':
      return row.value_text ?? '--';
    case 'date':
      return row.value_date
        ? new Date(row.value_date).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        : '--';
    case 'boolean':
      return row.value_boolean === 1 ? 'Yes' : row.value_boolean === 0 ? 'No' : '--';
    default:
      return '--';
  }
}

function formatRecordedAt(isoString: string): string {
  const d = new Date(isoString);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── Metric Tile ───

function MetricTile({ label, value, unit }: { label: string; value: string; unit: string }) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: '13px',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-1)',
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-regular)',
          lineHeight: 'var(--line-height-sm)',
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semi)',
          lineHeight: 'var(--line-height-lg)',
          letterSpacing: '-0.44px',
          color: 'var(--text)',
        }}
      >
        {value}
      </span>
      {unit && (
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-regular)',
            lineHeight: 'var(--line-height-sm)',
            color: 'var(--text-placeholder)',
          }}
        >
          {unit}
        </span>
      )}
    </div>
  );
}

// ─── Section ───

function VitalSection({
  title,
  entries,
  borderColor,
  bgGradient,
}: {
  title: string;
  entries: PatientVitalEntryWithDefinitionRow[];
  borderColor: string;
  bgGradient: string;
}) {
  if (entries.length === 0) return null;
  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        border: `1px solid ${borderColor}`,
        padding: 'var(--sp-5)',
        background: bgGradient,
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-base)',
          fontWeight: 'var(--font-weight-semi)',
          lineHeight: 'var(--line-height-base)',
          letterSpacing: '-0.15px',
          color: 'var(--text)',
          display: 'block',
          marginBottom: 'var(--sp-4)',
        }}
      >
        {title}
      </span>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--sp-3)',
        }}
      >
        {entries.map((row) => (
          <MetricTile
            key={row.id}
            label={row.name}
            value={formatVitalValue(row)}
            unit={row.unit ?? ''}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Empty State ───

const emptyContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--sp-3)',
  padding: 'var(--sp-8) var(--sp-6)',
  borderRadius: 'var(--radius-lg)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
};

// ─── History Session Card ───

function EncounterBadge({ source }: { source: string | null }) {
  if (!source || source === 'patient') return null;
  const label = source === 'encounter' ? 'Encounter' : source === 'queue' ? 'Queue' : source;
  return (
    <span
      style={{
        fontSize: 10,
        padding: '1px 6px',
        borderRadius: 'var(--radius-xs)',
        background: 'var(--color-info-bg, #dbeafe)',
        color: 'var(--color-info, #1e40af)',
        fontWeight: 'var(--font-weight-medium)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

function HistorySessionCard({
  timestamp,
  entries,
}: {
  timestamp: string;
  entries: PatientVitalEntryWithDefinitionRow[];
}) {
  const source = entries[0]?.source ?? null;
  return (
    <div
      style={{
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: 'var(--sp-3) var(--sp-4)',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-base)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            lineHeight: 'var(--line-height-sm)',
            color: 'var(--text-muted)',
          }}
        >
          {formatRecordedAt(timestamp)}
        </span>
        <EncounterBadge source={source} />
      </div>
      <div style={{ padding: 'var(--sp-2) 0' }}>
        {entries.map((row) => (
          <div
            key={row.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--sp-2) var(--sp-4)',
            }}
          >
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                lineHeight: 'var(--line-height-sm)',
              }}
            >
              {row.name}
            </span>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text)',
                lineHeight: 'var(--line-height-sm)',
              }}
            >
              {formatVitalValue(row)}
              {row.unit ? ` ${row.unit}` : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Vitals Detail View (sidebar content) ───

export function VitalsDetailView({
  onAddVitals,
  latestVitals,
  historyRows = [],
}: {
  onAddVitals: () => void;
  latestVitals: LatestVitalsGrouped | null;
  historyRows?: PatientVitalEntryWithDefinitionRow[];
}) {
  // Group history rows by recorded_at session
  const historySessions = useMemo(() => {
    if (historyRows.length === 0) return [];
    const map = new Map<string, PatientVitalEntryWithDefinitionRow[]>();
    for (const row of historyRows) {
      const existing = map.get(row.recorded_at);
      if (existing) existing.push(row);
      else map.set(row.recorded_at, [row]);
    }
    // Already sorted by recorded_at DESC from the query
    return Array.from(map.entries()).map(([ts, entries]) => ({ timestamp: ts, entries }));
  }, [historyRows]);
  return (
    <>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-lg)',
            letterSpacing: '-0.44px',
            color: 'var(--text)',
          }}
        >
          Vitals
        </span>
        <button
          onClick={onAddVitals}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            height: 30,
            padding: '0 var(--sp-3)',
            borderRadius: 'var(--radius-full)',
            border: '1px solid var(--color-danger-border)',
            background: 'transparent',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            lineHeight: 'var(--line-height-sm)',
            color: 'var(--color-danger)',
            cursor: 'pointer',
          }}
        >
          <NavIcon name="plus" /> Record Vitals
        </button>
      </div>

      {latestVitals ? (
        <>
          {/* Recorded timestamp */}
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            Recorded {formatRecordedAt(latestVitals.recordedAt)}
            <EncounterBadge
              source={latestVitals.primary[0]?.source ?? latestVitals.additional[0]?.source ?? null}
            />
          </div>

          {/* Primary Vitals */}
          <VitalSection
            title="Primary Vitals"
            entries={latestVitals.primary}
            borderColor="var(--color-info-border)"
            bgGradient="linear-gradient(to bottom, var(--color-info-bg), var(--bg-surface))"
          />

          {/* Additional Measurements */}
          <VitalSection
            title="Additional Measurements"
            entries={latestVitals.additional}
            borderColor="var(--border)"
            bgGradient="var(--bg-surface)"
          />

          {/* Recent History */}
          {historySessions.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
                <div style={{ color: 'var(--text-muted)' }}>
                  <NavIcon name="clock" />
                </div>
                <span
                  style={{
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-semi)',
                    lineHeight: 'var(--line-height-base)',
                    letterSpacing: '-0.15px',
                    color: 'var(--text)',
                  }}
                >
                  Recent History
                </span>
              </div>
              {historySessions.map((session) => (
                <HistorySessionCard
                  key={session.timestamp}
                  timestamp={session.timestamp}
                  entries={session.entries}
                />
              ))}
            </div>
          )}
        </>
      ) : (
        <div style={emptyContainerStyle}>
          <div style={{ color: 'var(--text-muted)' }}>
            <NavIcon name="heart" />
          </div>
          <span
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text)',
            }}
          >
            No vitals recorded yet
          </span>
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              textAlign: 'center',
            }}
          >
            Click &ldquo;Record Vitals&rdquo; to add the first reading.
          </span>
        </div>
      )}
    </>
  );
}
